import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type {
  ExhibitorVerification,
  Participation,
  Representation,
  PrintOrder,
  OrderPayment,
  BadgeRequestForm,
  ExhibitionPricing,
} from "../Component/Application/BadgeRequest/badge-request"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}
    return config
  },
  (error) => Promise.reject(error),
)

// Add interface for getUserOrders parameters
interface GetUserOrdersParams {
  page?: number
  page_size?: number
  search?: string
  status?: string
}

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

export const BadgeRequestService = {
  // registerMyself: async (data: any): Promise<any> => {
  //   const response = await api.post("/api/badges/register-myself/", data)
  //   return response.data
  // },

  // getVerifiedExhibitorStatus: async (): Promise<any> => {
  //   const response = await api.get("/api/badges/verified-exhibitor-status/")
  //   return response.data
  // },

  getVerifiedExhibitors: async (): Promise<ExhibitorVerification[]> => {
    const response = await api.get("/api/badges/badge-request/verified-exhibitors/")
    return response.data
  },

  // Get active participations for an exhibitor
  getExhibitorParticipations: async (exhibitorId: number): Promise<Participation[]> => {
    const response = await api.get(`/api/badges/badge-request/exhibitor-participations/${exhibitorId}/`)
    return response.data
  },

  // Get representations for a participation
  getParticipationRepresentations: async (participationId: number): Promise<Representation[]> => {
    const response = await api.get(`/api/badges/badge-request/participation-representations/${participationId}/`)
    return response.data
  },

  // NEW: Get ordered representations for a participation (for reprint requests)
  getParticipationOrderedRepresentations: async (participationId: number): Promise<Representation[]> => {
    const response = await api.get(
      `/api/badges/badge-requests/participation/${participationId}/ordered-representations/`,
    )
    return response.data
  },

  // NEW: Get staff who already have orders and can request reprints for a specific participation
  getOrderedStaffForReprint: async (participationId: number): Promise<any[]> => {
    const response = await api.get(
      `/api/badges/badge-requests/participation/${participationId}/ordered-staff-for-reprint/`,
    )
    return response.data
  },

  // Get exhibition pricing
  getExhibitionPricing: async (exhibitionId: number): Promise<ExhibitionPricing> => {
    const response = await api.get(`/api/badges/badge-request/exhibition-pricing/${exhibitionId}/`)
    return response.data
  },

  // Submit badge request
  submitBadgeRequest: async (data: BadgeRequestForm): Promise<PrintOrder> => {
    const response = await api.post("/api/badges/badge-request/submit/", data)
    return response.data
  },

  // NEW: Submit reprint request (convenience method)
  submitBadgeReprintRequest: async (data: {
    participation_id: number
    selected_staff: number[]
  }): Promise<PrintOrder> => {
    const requestData = {
      participation_id: data.participation_id,
      selected_representations: data.selected_staff,
      is_reprint_request: true,
    }
    const response = await api.post("/api/badges/badge-request/submit/", requestData)
    return response.data
  },

  // UPDATED: Get user's orders with pagination and filtering support
  getUserOrders: async (params?: GetUserOrdersParams): Promise<PaginatedResponse<PrintOrder>> => {
    // Build query parameters
    const queryParams = new URLSearchParams()

    if (params?.page) {
      queryParams.append("page", params.page.toString())
    }

    if (params?.page_size) {
      queryParams.append("page_size", params.page_size.toString())
    }

    if (params?.search && params.search.trim()) {
      queryParams.append("search", params.search.trim())
    }

    if (params?.status && params.status !== "all") {
      queryParams.append("status", params.status)
    }

    const url = queryParams.toString()
      ? `/api/badges/badge-request/user-orders/?${queryParams.toString()}`
      : "/api/badges/badge-request/user-orders/"
    const response = await api.get(url)
    return response.data
  },

  // Submit payment with FormData support
  submitPayment: async (
    orderId: number,
    paymentData: FormData | Omit<OrderPayment, "id" | "print_order" | "created_at">,
  ): Promise<OrderPayment> => {
    // Check if paymentData is FormData
    if (paymentData instanceof FormData) {
      // For FormData, we need to handle it differently
      const response = await api.post(`/api/badges/badge-request/submit-payment/${orderId}/`, paymentData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      return response.data
    } else {
      // For regular JSON data
      const response = await api.post(`/api/badges/badge-request/submit-payment/${orderId}/`, paymentData)
      return response.data
    }
  },

  // Get orders for verification with pagination and filters
  getOrdersForVerification: async (params: GetUserOrdersParams = {}): Promise<PaginatedResponse<PrintOrder>> => {
    console.log("=== CALLING getOrdersForVerification ===")
    console.log("Params:", params)

    const queryParams = new URLSearchParams()

    if (params.page) queryParams.append("page", params.page.toString())
    if (params.page_size) queryParams.append("page_size", params.page_size.toString())
    if (params.search) queryParams.append("search", params.search)
    if (params.status && params.status !== "all") queryParams.append("status", params.status)

    const url = `/api/badges/badge-request/orders-for-verification/?${queryParams.toString()}`
    console.log("Request URL:", url)

    const response = await api.get(url)
    console.log("Response:", response.data)
    return response.data
  },

  // Approve order (finance)
  approveOrder: async (orderId: number): Promise<PrintOrder> => {
    const response = await api.put(`/api/badges/badge-request/approve-order/${orderId}/`)
    return response.data
  },

  // Reject order (finance)
  rejectOrder: async (orderId: number, reason: string): Promise<PrintOrder> => {
    const response = await api.put(`/api/badges/badge-request/reject-order/${orderId}/`, { reason })
    return response.data
  },
}
